
import { useCallback, useMemo, useRef } from 'react';
import { logger } from '@/utils/logger';

// Debounce hook
export const useDebounce = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }) as T,
    [callback, delay]
  );
};

// Throttle hook
export const useThrottle = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T => {
  const lastRun = useRef(Date.now());

  return useCallback(
    ((...args: Parameters<T>) => {
      if (Date.now() - lastRun.current >= delay) {
        callback(...args);
        lastRun.current = Date.now();
      }
    }) as T,
    [callback, delay]
  );
};

// Memoized calculations
export const useMemoizedCalculations = (data: any[], dependencies: any[]) => {
  return useMemo(() => {
    // Expensive calculations here
    const total = data.reduce((sum, item) => sum + (item.amount || 0), 0);
    const count = data.length;
    const average = count > 0 ? total / count : 0;

    return { total, count, average };
  }, [data, ...dependencies]);
};

// Performance monitoring - uses logger.debug to avoid console spam
export const usePerformanceMonitor = (componentName: string) => {
  const renderStartTime = useRef(Date.now());

  const logRenderTime = useCallback(() => {
    const renderTime = Date.now() - renderStartTime.current;
    // Only log in development if render takes more than 500ms (increased threshold)
    if (renderTime > 500) {
      logger.debug(`[Performance] ${componentName} render took ${renderTime}ms`);
    }
    renderStartTime.current = Date.now();
  }, [componentName]);

  return { logRenderTime };
};
